import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  FileText, 
  Eye, 
  Heart, 
  Plus, 
  TrendingUp, 
  Calendar,
  Users,
  BarChart3,
  AlertTriangle,
  Star,
  BookOpen,
  Flame,
  User,
  MapPin,
  Clock,
  Loader2,
  Users2
} from 'lucide-react'
import { format } from 'date-fns'
import apiService from '../services/api'
import { toast } from 'react-hot-toast'
import UserTrendsChart from '../components/charts/UserTrendsChart'
import BookingTrendsChart from '../components/charts/BookingTrendsChart'

function Dashboard() {
  // State management
  const [stats, setStats] = useState({
    totalBlogs: 0,
    publishedBlogs: 0,
    draftBlogs: 0,
    totalViews: 0,
    totalLikes: 0,
    pendingSubmissions: 0,
    totalPandits: 0,
    totalBookings: 0,
    activeServices: 0,
    pendingVerifications: 0,
    totalRevenue: 0,
    newYajmansToday: 0,
    bookingsToday: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [bookingTrends, setBookingTrends] = useState({})
  const [userTrends, setUserTrends] = useState({})
  const [latestBookings, setLatestBookings] = useState([])

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Fetch dashboard stats, booking trends, and user trends with individual error handling
        const [statsResponse, trendsResponse, userTrendsResponse] = await Promise.allSettled([
          apiService.getDashboardStats(),
          apiService.getBookingTrends(7),
          apiService.getUserTrends(14)
        ])
        
        // Handle dashboard stats
        if (statsResponse.status === 'fulfilled' && statsResponse.value.success) {
          const dashboardData = statsResponse.value.data
          
          // Map backend data to frontend state
          setStats({
            totalBlogs: dashboardData.summary?.totalBooks || 0,
            publishedBlogs: dashboardData.summary?.totalBooks || 0,
            draftBlogs: 0,
            totalViews: 0,
            totalLikes: 0,
            pendingSubmissions: dashboardData.summary?.totalYajmans || 0,
            totalPandits: dashboardData.summary?.totalPandits || 0,
            totalBookings: dashboardData.summary?.totalBookings || 0,
            activeServices: dashboardData.summary?.totalServices || 0,
            pendingVerifications: dashboardData.todayStats?.newPandits || 0,
            totalRevenue: dashboardData.summary?.totalRevenue || 0,
            newYajmansToday: dashboardData.todayStats?.newYajmans || 0,
            bookingsToday: dashboardData.todayStats?.bookingsToday || 0
          })
          
          // Set latest bookings data
          setLatestBookings(dashboardData.latestBookings || [])
        } else {
          console.warn('Failed to fetch dashboard stats:', statsResponse.reason)
        }
        
        // Handle booking trends
        if (trendsResponse.status === 'fulfilled' && trendsResponse.value.success) {
          setBookingTrends(trendsResponse.value.data || {})
        } else {
          console.warn('Failed to fetch booking trends:', trendsResponse.reason)
          setBookingTrends({}) // Set empty object so chart shows "no data" state
        }

        // Handle user trends
        if (userTrendsResponse.status === 'fulfilled' && userTrendsResponse.value.success) {
          setUserTrends(userTrendsResponse.value.data || {})
        } else {
          console.warn('Failed to fetch user trends:', userTrendsResponse.reason)
          setUserTrends({}) // Set empty object so chart shows "no data" state
        }
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
        setError(err.message)
        toast.error('Failed to load dashboard data')
      } finally {
        setLoading(false)
      }
    }
    
    fetchDashboardData()
  }, [])

  const statCards = [
    {
      title: 'Spiritual Books',
      value: stats.totalBlogs,
      icon: BookOpen,
      color: 'bg-orange-500',
      change: loading ? '...' : `${stats.totalBlogs} total`,
      changeType: 'positive',
      description: 'E-Library collection'
    },
    {
      title: 'Verified Pandits',
      value: stats.totalPandits,
      icon: User,
      color: 'bg-amber-600',
      change: loading ? '...' : (stats.pendingVerifications > 0 ? `+${stats.pendingVerifications} today` : 'No new today'),
      changeType: stats.pendingVerifications > 0 ? 'positive' : 'neutral',
      description: 'Active spiritual guides'
    },
    {
      title: 'Total Bookings',
      value: stats.totalBookings,
      icon: Calendar,
      color: 'bg-green-600',
      change: loading ? '...' : (stats.bookingsToday > 0 ? `${stats.bookingsToday} today` : 'None today'),
      changeType: stats.bookingsToday > 0 ? 'positive' : 'neutral',
      description: 'Scheduled ceremonies'
    },
    {
      title: 'Spiritual Services',
      value: stats.activeServices,
      icon: Flame,
      color: 'bg-red-600',
      change: loading ? '...' : `${stats.activeServices} available`,
      changeType: 'positive',
      description: 'Available rituals & pujas'
    },
    {
      title: 'Total Revenue',
      value: `₹${stats.totalRevenue.toLocaleString()}`,
      icon: TrendingUp,
      color: 'bg-purple-600',
      change: loading ? '...' : (stats.totalRevenue > 0 ? 'From bookings' : 'No revenue yet'),
      changeType: stats.totalRevenue > 0 ? 'positive' : 'neutral',
      description: 'Platform earnings'
    },
    {
      title: 'Total Yajmans',
      value: stats.pendingSubmissions,
      icon: Users,
      color: 'bg-blue-500',
      change: loading ? '...' : (stats.newYajmansToday > 0 ? `+${stats.newYajmansToday} today` : 'No new today'),
      changeType: stats.newYajmansToday > 0 ? 'positive' : 'neutral',
      description: 'Registered devotees'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header with spiritual greeting */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Vaidik Sanskar Dashboard
          </h1>
          <p className="mt-2 text-gray-600">
            Welcome to your spiritual platform management center
          </p>
          <div className="mt-1 text-sm text-orange-600 font-medium">
            🙏 Namaste! May your day be blessed with divine grace
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.location.reload()}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-lg text-sm font-medium text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TrendingUp className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">Failed to load dashboard data: {error}</p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
          // Loading skeleton
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="mt-4 flex items-center">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-3 bg-gray-200 rounded w-24 ml-2"></div>
              </div>
            </div>
          ))
        ) : (
          statCards.map((stat, index) => {
            const Icon = stat.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
                {/* Subtle pattern background */}
                <div className="absolute top-0 right-0 w-16 h-16 opacity-5">
                  <div className="w-full h-full bg-gradient-to-br from-orange-400 to-red-500 rounded-full transform translate-x-8 -translate-y-8"></div>
                </div>
                
                <div className="flex items-center justify-between relative z-10">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2 flex items-center">
                      {stat.value}
                      {loading && <Loader2 className="w-4 h-4 ml-2 animate-spin text-gray-400" />}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg shadow-sm`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 
                    stat.changeType === 'attention' ? 'text-orange-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">from last month</span>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <UserTrendsChart 
          data={userTrends}
          loading={loading}
          title="User Registration Trends"
        />
        <BookingTrendsChart 
          data={bookingTrends}
          loading={loading}
          title="Booking & Revenue Trends"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="w-5 h-5 mr-2 text-orange-600" />
                Recent Activity
              </h2>
              <Link 
                to="/yajman" 
                className="text-orange-600 hover:text-orange-700 text-sm font-medium transition-colors duration-200"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-lg animate-pulse">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : latestBookings.length > 0 ? (
              <div className="space-y-4">
                {latestBookings.slice(0, 5).map((user) => (
                  <div key={user.id} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-orange-50 transition-colors duration-200 border border-orange-100">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center shadow-sm">
                        <User className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {user.profile?.name || 'New User'}
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {format(new Date(user.createdAt), 'MMM dd, yyyy')}
                        </span>
                        <span className="flex items-center">
                          <span className={`w-2 h-2 rounded-full mr-1 ${user.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                          {user.status}
                        </span>
                        {user.phone && (
                          <span className="flex items-center">
                            <span className="mr-1">📱</span>
                            {user.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-orange-300 mx-auto mb-4" />
                <p className="text-gray-500">No recent activity</p>
                <p className="text-xs text-gray-400 mt-1">
                  New user registrations will appear here
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions for Spiritual Platform */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Flame className="w-5 h-5 mr-2 text-orange-600" />
              Sacred Management
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <Link
                to="/elib/add"
                className="flex items-center p-4 rounded-lg border-2 border-dashed border-orange-300 hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 group"
              >
                <div className="flex-shrink-0">
                  <Plus className="w-8 h-8 text-orange-400 group-hover:text-orange-600 transition-colors duration-200" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-orange-600 transition-colors duration-200">
                    Add A Book
                  </p>
                  <p className="text-xs text-gray-500">
                    Add a new book to the library
                  </p>
                </div>
              </Link>

              <Link
                to="/pandits"
                className="flex items-center p-4 rounded-lg border border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 group"
              >
                <div className="flex-shrink-0">
                  <User className="w-8 h-8 text-orange-400 group-hover:text-orange-600 transition-colors duration-200" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-orange-600 transition-colors duration-200">
                    Manage Pandits
                  </p>
                  <p className="text-xs text-gray-500">
                    Verify and manage spiritual guides
                  </p>
                </div>
              </Link>

              <Link
                to="/featured-pandits"
                className="flex items-center p-4 rounded-lg border border-gray-200 hover:border-yellow-500 hover:bg-yellow-50 transition-all duration-200 group"
              >
                <div className="flex-shrink-0">
                  <Star className="w-8 h-8 text-yellow-400 group-hover:text-yellow-600 transition-colors duration-200" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-yellow-600 transition-colors duration-200">
                    Featured Pandits
                  </p>
                  <p className="text-xs text-gray-500">
                    Premium spiritual guide listings
                  </p>
                </div>
              </Link>

              <Link
                to="/unverified-pandits"
                className="flex items-center p-4 rounded-lg border border-gray-200 hover:border-red-500 hover:bg-red-50 transition-all duration-200 group"
              >
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-8 h-8 text-red-400 group-hover:text-red-600 transition-colors duration-200" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-red-600 transition-colors duration-200">
                    Pending Verification
                  </p>
                  <p className="text-xs text-gray-500">
                    {stats.pendingVerifications || 8} applications awaiting review
                  </p>
                </div>
              </Link>

              <Link
                to="/bookings"
                className="flex items-center p-4 rounded-lg border border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 group"
              >
                <div className="flex-shrink-0">
                  <Calendar className="w-8 h-8 text-orange-400 group-hover:text-orange-600 transition-colors duration-200" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-orange-600 transition-colors duration-200">
                    Ceremony Bookings
                  </p>
                  <p className="text-xs text-gray-500">
                    {stats.totalBookings || 156} scheduled rituals & pujas
                  </p>
                </div>
              </Link>

              <Link
                to="/services"
                className="flex items-center p-4 rounded-lg border border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 group"
              >
                <div className="flex-shrink-0">
                  <Flame className="w-8 h-8 text-orange-400 group-hover:text-orange-600 transition-colors duration-200" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-orange-600 transition-colors duration-200">
                    Spiritual Services
                  </p>
                  <p className="text-xs text-gray-500">
                    Manage rituals, pujas & ceremonies
                  </p>
                </div>
              </Link>

              <Link
                to="/donations"
                className="flex items-center p-4 rounded-lg border border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 group"
              >
                <div className="flex-shrink-0">
                  <Users2 className="w-8 h-8 text-orange-400 group-hover:text-orange-600 transition-colors duration-200" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900 group-hover:text-orange-600 transition-colors duration-200">
                    User management
                  </p>
                  <p className="text-xs text-gray-500">
                    Manage users and their roles
                  </p>
                </div>
              </Link>

              <div className="flex items-center p-4 rounded-lg border border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
                <div className="flex-shrink-0">
                  <Star className="w-8 h-8 text-orange-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-orange-600">
                    Panchang & Astrology (Coming Soon)
                  </p>
                  <p className="text-xs text-orange-500">
                    Daily spiritual insights & muhurat
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Daily Spiritual Insights */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl shadow-sm border border-orange-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Star className="w-5 h-5 mr-2 text-orange-600" />
            Today's Spiritual Insights
          </h2>
          <div className="text-sm text-orange-600 font-medium">
            {format(new Date(), 'EEEE, MMMM dd, yyyy')}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-4 border border-orange-200">
            <h3 className="font-semibold text-orange-700 mb-2 flex items-center">
              <span className="mr-2">🌅</span>
              Today's Muhurat
            </h3>
            <p className="text-sm text-gray-600">
              <strong>Sunrise:</strong> 6:42 AM<br/>
              <strong>Sunset:</strong> 6:18 PM<br/>
              <strong>Best Time for Prayers:</strong> 7:00 - 8:00 AM
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-orange-200">
            <h3 className="font-semibold text-orange-700 mb-2 flex items-center">
              <span className="mr-2">🌙</span>
              Tithi & Nakshatra
            </h3>
            <p className="text-sm text-gray-600">
              <strong>Tithi:</strong> Shukla Paksha Dvitiya<br/>
              <strong>Nakshatra:</strong> Rohini<br/>
              <strong>Yoga:</strong> Saubhagya
            </p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-orange-200">
            <h3 className="font-semibold text-orange-700 mb-2 flex items-center">
              <span className="mr-2">💎</span>
              Spiritual Reminder
            </h3>
            <p className="text-sm text-gray-600 italic">
              "सर्वे भवन्तु सुखिनः सर्वे सन्तु निरामयाः"<br/>
              <span className="text-xs">May all beings be happy and healthy</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
